import React from "react";
import { Button } from "@/components/Calendar/ui/button";
import { Calendar } from "@/components/Calendar/ui/calendar";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Filter as FilterIcon,
  RefreshCw,
  ExternalLink,
  Plus,
  CheckSquare,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  useScheduleData,
  type UseScheduleDataReturn,
  type Booking,
  type Task,
  getMemberByFullName,
  isMemberWorkingAt,
  isResourceAvailable,
  groupBookingsByDay,
  getDayShortName,
} from "@/components/Calendar/scheduleData";
import { ServiceTypeFilter } from "./schedule/ServiceTypeFilter";
import { PharmacistFilter } from "./schedule/PharmacistFilter";
import { RoomFilter } from "./schedule/RoomFilter";
import { BlockOutTypesFilter } from "./schedule/BlockOutTypesFilter";
import { ServiceTypesInfo } from "./schedule/ServiceTypesInfo";
import { ScheduleFilters } from "./schedule/ScheduleFilters";
import { ViewSettings } from "./schedule/ViewSettings";
import { SaveDefaultViewBar } from "./schedule/SaveDefaultViewBar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Calendar/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/Calendar/ui/dropdown-menu";
import {
  saveSchedulePreferences,
  loadSchedulePreferences,
  type SchedulePreferences,
} from "./schedule/schedulePreferences";
import { BookingModal } from "../Scheduler/schedule/BookingModal";
import { CalendarDayCell } from "./schedule/CalendarDayCell";
import { TimeSlotCell } from "./schedule/TimeSlotCell";
import { BlockOutModal } from "../Scheduler/schedule/BlockOutModal";
import { TaskModal } from "./schedule/TaskModal";
import ScheduleSkeleton from "./scheduleSkeleton";
import {
  saveGoogleEvents,
  getGoogleEvents,
} from "@/dispatchers/scheduleEventsDispatchers";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Calendar/ui/dialog";
import { Button as UIButton } from "@/components/Calendar/ui/button";
import { Switch } from "@/components/Calendar/ui/switch";
import { Label } from "@/components/Calendar/ui/label";

function getPractitionerInitials(fullName: string) {
  const parts = fullName.split(" ").filter(Boolean);
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

interface ScheduleProps {
  isDarkMode?: boolean;
  initialDateString?: string;
  onGoToPatient?: (patientName: string) => void;
}

interface GoogleCalendarSyncState {
  provider: "google";
  email: string;
  syncedAt: string;
  accessToken: string;
}

interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  startDateTime?: string;
  endDateTime?: string;
  date: string;
  time: string;
  duration: number;
  htmlLink?: string;
  attendees?: Array<{
    email: string;
    self?: boolean;
    responseStatus?: string;
  }>;
}

const findResourceByName = (name: string, resourcesList: any[]) => {
  if (!resourcesList || !Array.isArray(resourcesList) || !name) return null;
  const normName = name.trim().toLowerCase();
  return (
    resourcesList.find((r: any) => {
      const matchName = r.name && r.name.trim().toLowerCase() === normName;
      const matchId = r.id && String(r.id).trim().toLowerCase() === normName;
      return matchName || matchId;
    }) || null
  );
};

const checkSlotWorkingStatus = (
  date: Date,
  timeString: string | undefined,
  columnMode: "practitioner" | "day" | "services" | "room" | "day-practitioner",
  columnValue: string,
  selectedPharmacists: string[],
  selectedRooms: string[],
  selectedServiceTypes: string[],
  resourcesList: any[],
  pharmacistNamesList: string[],
  roomNamesList: string[],
): boolean => {
  const dayOfWeek = date.getDay();

  if (columnMode === "practitioner" || columnMode === "day-practitioner") {
    const pharmacistName = columnValue;
    const resource = findResourceByName(pharmacistName, resourcesList);
    let isWorking = true;

    if (resource) {
      isWorking = isResourceAvailable(
        resource,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    } else {
      const member = getMemberByFullName(pharmacistName);
      isWorking = timeString
        ? isMemberWorkingAt(member, dayOfWeek, timeString)
        : true;
    }

    // If specific room filter is active, check room availability as well
    if (isWorking && selectedRooms && selectedRooms.length > 0) {
      const isAnyRoomAvail = selectedRooms.some((roomName) => {
        const roomRes = findResourceByName(roomName, resourcesList);
        return isResourceAvailable(
          roomRes,
          dayOfWeek,
          timeString,
          selectedServiceTypes,
        );
      });
      if (!isAnyRoomAvail) {
        isWorking = false;
      }
    }

    return isWorking;
  }

  if (columnMode === "room") {
    const roomName = columnValue;
    const resource = findResourceByName(roomName, resourcesList);
    let isWorking = true;

    if (resource) {
      isWorking = isResourceAvailable(
        resource,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    }

    // If specific pharmacist filter is active, check pharmacist availability as well
    if (isWorking && selectedPharmacists && selectedPharmacists.length > 0) {
      const isAnyPharmAvail = selectedPharmacists.some((pharmName) => {
        const pharmRes = findResourceByName(pharmName, resourcesList);
        return isResourceAvailable(
          pharmRes,
          dayOfWeek,
          timeString,
          selectedServiceTypes,
        );
      });
      if (!isAnyPharmAvail) {
        isWorking = false;
      }
    }

    return isWorking;
  }

  if (columnMode === "services") {
    const serviceType = columnValue;

    const activePharmacists =
      selectedPharmacists.length > 0
        ? selectedPharmacists
        : pharmacistNamesList;
    const activeRooms =
      selectedRooms.length > 0 ? selectedRooms : roomNamesList;

    const pharmAvail =
      activePharmacists.length === 0 ||
      activePharmacists.some((pName) => {
        const pRes = findResourceByName(pName, resourcesList);
        return isResourceAvailable(pRes, dayOfWeek, timeString, [serviceType]);
      });

    const roomAvail =
      activeRooms.length === 0 ||
      activeRooms.some((rName) => {
        const rRes = findResourceByName(rName, resourcesList);
        return isResourceAvailable(rRes, dayOfWeek, timeString, [serviceType]);
      });

    return pharmAvail && roomAvail;
  }

  // columnMode === "day" or Month View
  const activePharmacists =
    selectedPharmacists.length > 0 ? selectedPharmacists : pharmacistNamesList;
  const activeRooms = selectedRooms.length > 0 ? selectedRooms : roomNamesList;

  const pharmAvail =
    activePharmacists.length === 0 ||
    activePharmacists.some((pName) => {
      const pRes = findResourceByName(pName, resourcesList);
      return isResourceAvailable(
        pRes,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    });

  const roomAvail =
    activeRooms.length === 0 ||
    activeRooms.some((rName) => {
      const rRes = findResourceByName(rName, resourcesList);
      return isResourceAvailable(
        rRes,
        dayOfWeek,
        timeString,
        selectedServiceTypes,
      );
    });

  return pharmAvail && roomAvail;
};

export default function ScheduleCalendar({
  initialDateString,
  onGoToPatient,
}: ScheduleProps) {
  const s = useScheduleData();
  const routerNavigate = useNavigate();
  const [displayMode, setDisplayMode] = useState<"calendar" | "list">(
    "calendar",
  );
  const [columnMode, setColumnMode] = useState<
    "practitioner" | "day" | "services" | "room" | "day-practitioner"
  >("day-practitioner");
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showBlockOutModal, setShowBlockOutModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [rescheduleData, setRescheduleData] = useState<{
    booking: Booking;
    newDate: string;
    newTime?: string;
  } | null>(null);
  const [notifyPatient, setNotifyPatient] = useState(true);
  const [notifiedBookingId, setNotifiedBookingId] = useState<string | null>(
    null,
  );
  const [notificationType, setNotificationType] = useState<"message" | "check">(
    "check",
  );
  const [showSaveDefaultBar, setShowSaveDefaultBar] = useState(false);
  const [initialPreferences, setInitialPreferences] =
    useState<SchedulePreferences | null>(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const tokenClientRef = React.useRef<any>(null);
  const [googleSyncState, setGoogleSyncState] =
    useState<GoogleCalendarSyncState | null>(null);
  const [googleSyncError, setGoogleSyncError] = useState<string | null>(null);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);

  const getBookingsForDate = (dateString: string) =>
    getFilteredBookingsForDate(dateString);

  const getBookingsForTimeSlot = (dateString: string, timeSlot: number) => {
    const slotMinutes = Math.round(timeSlot * 60);

    return getFilteredBookingsForTimeSlot(dateString, timeSlot).filter(
      (booking) => {
        const [bookingHour, bookingMinute] = (booking.time || "00:00")
          .split(":")
          .map(Number);
        const bookingStart = (bookingHour || 0) * 60 + (bookingMinute || 0);
        return bookingStart >= slotMinutes && bookingStart < slotMinutes + 30;
      },
    );
  };

  const mapGoogleApiEvent = (item: any): GoogleCalendarEvent => {
    const start = item.start?.dateTime || item.start?.date;
    const end = item.end?.dateTime || item.end?.date;
    const startDate = start ? new Date(start) : new Date();
    const endDate = end
      ? new Date(end)
      : new Date(startDate.getTime() + 60 * 60000);
    const duration = Math.max(
      30,
      Math.round((endDate.getTime() - startDate.getTime()) / 60000),
    );
    const allDay = !!item.start?.date && !item.start?.dateTime;
    const time = allDay
      ? "All day"
      : `${String(startDate.getHours()).padStart(2, "0")}:${String(
          startDate.getMinutes(),
        ).padStart(2, "0")}`;

    return {
      id: item.id,
      summary: item.summary || "Untitled event",
      description: item.description,
      startDateTime: item.start?.dateTime,
      endDateTime: item.end?.dateTime,
      date: `${startDate.getFullYear()}-${String(
        startDate.getMonth() + 1,
      ).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`,
      time,
      duration,
      htmlLink: item.htmlLink,
      attendees: item.attendees || [],
    };
  };

  /**
   * Filter Google Calendar events based on attendee email matching resourceId
   * @param events - Array of Google Calendar events to filter
   * @param resourceId - Email address to match against attendees (typically user's email)
   * @returns Filtered events where the resourceId email is in the attendees list
   */
  const filterEventsByResourceId = (
    events: GoogleCalendarEvent[],
    resourceId: string,
  ): GoogleCalendarEvent[] => {
    const normalizedResourceId = (resourceId || "").trim().toLowerCase();

    if (!normalizedResourceId) return [];

    return events
      .map((event) => {
        const matchingAttendees = (event.attendees || []).filter((attendee) => {
          const attendeeEmail = (attendee.email || "").trim().toLowerCase();
          return attendeeEmail === normalizedResourceId;
        });

        if (matchingAttendees.length === 0) {
          return null;
        }

        return {
          ...event,
          attendees: matchingAttendees,
        };
      })
      .filter(Boolean) as GoogleCalendarEvent[];
  };

  /**
   * Get Google Calendar events for a specific time slot
   * @param dateString - Date in format YYYY-MM-DD
   * @param timeSlot - Hour in 24-hour format
   * @returns Google Calendar events that overlap with the time slot
   */
  const getGoogleEventsForTimeSlot = (dateString: string, timeSlot: number) => {
    const hour = Math.floor(timeSlot);
    const minutes = timeSlot % 1 === 0 ? 0 : 30;

    return googleCalendarEvents.filter((event) => {
      // Match by date
      if (event.date !== dateString) return false;

      // Extract time from event.time (format: "HH:mm")
      if (event.time === "All day") return false;

      const [eventHour, eventMinutes = 0] = event.time.split(":").map(Number);
      return eventHour === hour && eventMinutes === minutes;
    });
  };

  /**
   * Organize Google Calendar events by date for week view
   * @param dates - Array of date strings in format YYYY-MM-DD
   * @returns Object with dates as keys and arrays of events as values
   */
  const getWeekGoogleEventsByDate = (
    dates: string[],
  ): Record<string, GoogleCalendarEvent[]> => {
    const eventsByDate: Record<string, GoogleCalendarEvent[]> = {};
    dates.forEach((dateString) => {
      eventsByDate[dateString] = googleCalendarEvents.filter(
        (event) => event.date === dateString,
      );
    });
    return eventsByDate;
  };

  // Get Google Calendar events for a specific date (month view)
  const getDayGoogleEvents = (dateString: string): GoogleCalendarEvent[] =>
    googleCalendarEvents.filter((event) => event.date === dateString);

  const fetchGoogleCalendarEvents = React.useCallback(async (token: string) => {
    const response = await fetch(
      "https://www.googleapis.com/calendar/v3/calendars/primary/events?maxResults=20&orderBy=startTime&singleEvents=true",
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Unable to fetch Google Calendar events.");
    }

    const data = await response.json();
    return Array.isArray(data.items) ? data.items : [];
  }, []);

  const initializeGoogleSync = React.useCallback(() => {
    const google = (window as any).google;
    if (!google?.accounts?.oauth2?.initTokenClient) {
      setGoogleSyncError(
        "Google sign-in is not available in this browser session.",
      );
      return;
    }

    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
    if (!googleClientId) {
      setGoogleSyncError(
        "Google Client ID is missing. Add VITE_GOOGLE_CLIENT_ID to your environment.",
      );
      return;
    }

    tokenClientRef.current = google.accounts.oauth2.initTokenClient({
      client_id: googleClientId,
      scope:
        "https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/userinfo.email",
      ux_mode: "popup",
      callback: async (response: any) => {
        setIsConnectingGoogle(false);

        if (response.error) {
          setGoogleSyncError("Google sign-in was cancelled or failed.");
          return;
        }

        const accessToken = response.access_token;
        if (!accessToken) {
          setGoogleSyncError("Google did not return an access token.");
          return;
        }

        try {
          const profileResponse = await fetch(
            "https://www.googleapis.com/oauth2/v2/userinfo",
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          );

          const profile = await profileResponse.json();

          const nextState: GoogleCalendarSyncState = {
            provider: "google",
            email: profile.email || "Google Calendar",
            syncedAt: new Date().toISOString(),
            accessToken,
          };

          localStorage.setItem(
            "clifton-care-google-calendar-sync",
            JSON.stringify(nextState),
          );
          setGoogleSyncState(nextState);
          setGoogleSyncError(null);

          const items = await fetchGoogleCalendarEvents(accessToken);
          const mappedEvents = items.map(mapGoogleApiEvent);
          const filteredEvents = filterEventsByResourceId(
            mappedEvents,
            currentUserEmail,
          );
          const payload = filteredEvents.map((event) => ({
            googleEventId: event.id,
            Summary: event.summary,
            Description: event.description,
            StartDateTime: event.startDateTime,
            EndDateTime: event.endDateTime,
            Date: event.date,
            Time: event.time,
            Duration: event.duration,
            HtmlLink: event.htmlLink,
            ResourceId: event.attendees?.[0]?.email || currentUserEmail,
          }));
          await saveGoogleEvents(payload);
          await getGoogleEvents();
        } catch (error) {
          console.error(error);
          setGoogleSyncError("Unable to connect to Google Calendar right now.");
        }
      },
    });
  }, [fetchGoogleCalendarEvents]);

  const requestGoogleAccessToken = async () => {
    if (!tokenClientRef.current) {
      initializeGoogleSync();
    }
    if (!tokenClientRef.current) {
      setGoogleSyncError("Google OAuth client is not ready.");
      return;
    }

    setIsConnectingGoogle(true);
    tokenClientRef.current.requestAccessToken({ prompt: "consent" });
  };

  // Do NOT auto-load saved Google sync on mount — default state is disconnected.
  // Connection will be established when the user clicks Connect and completes OAuth.

  const disconnectGoogle = () => {
    try {
      localStorage.removeItem("clifton-care-google-calendar-sync");
    } catch (e) {
      // ignore
    }
    setGoogleSyncState(null);
    setGoogleSyncError(null);
    tokenClientRef.current = null;
  };

  React.useEffect(() => {
    const google = (window as any).google;
    if (google?.accounts?.oauth2?.initTokenClient) {
      initializeGoogleSync();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => initializeGoogleSync();
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [initializeGoogleSync]);

  React.useEffect(() => {
    if (!googleSyncState?.accessToken) {
      return;
    }

    let isMounted = true;

    const syncGoogleEvents = async () => {
      try {
        const items = await fetchGoogleCalendarEvents(
          googleSyncState.accessToken,
        );
        if (isMounted) {
          const mappedEvents = items.map(mapGoogleApiEvent);
          const filteredEvents = filterEventsByResourceId(
            mappedEvents,
            currentUserEmail,
          );
          const payload = filteredEvents.map((event) => ({
            googleEventId: event.id,
            Summary: event.summary,
            Description: event.description,
            StartDateTime: event.startDateTime,
            EndDateTime: event.endDateTime,
            Date: event.date,
            Time: event.time,
            Duration: event.duration,
            HtmlLink: event.htmlLink,
            ResourceId: event.attendees?.[0]?.email || "currentUserEmail",
          }));
          await saveGoogleEvents(payload);
          await getGoogleEvents();
          setGoogleSyncError(null);
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setGoogleSyncError(
            "Live Google Calendar sync is temporarily unavailable.",
          );
        }
      }
    };

    syncGoogleEvents();
    const intervalId = window.setInterval(syncGoogleEvents, 60000);
    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [fetchGoogleCalendarEvents, googleSyncState?.accessToken]);

  // Generate calendar time slots from 08:00 through 23:30 (32 half-hour slots).
  const weekTimeSlots = React.useMemo(() => {
    return Array.from({ length: 32 }, (_, i) => 8 + i * 0.5);
  }, []);

  // Destructure everything we use from the hook result, with safe defaults
  const {
    isDarkMode = false,
    // theme / core
    isEventsLoading = false,
    currentDate = new Date(),
    view = "month",
    setView = () => {},
    calendarSelectedDate = new Date(),
    modalState = {
      isOpen: false,
      mode: null,
    } as UseScheduleDataReturn["modalState"],
    blockOutModalState = {
      isOpen: false,
      mode: null,
    } as UseScheduleDataReturn["blockOutModalState"],

    // data / labels
    dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    timeSlots = [],
    serviceColors = { Earwax: "", Travel: "", Weight: "", Covid: "" },
    serviceColorsFromData = {},
    serviceColorMap = {}, // API service colors (hex)
    serviceNameMap = {}, // Service ID to name mapping
    pharmacists = [],
    pharmacistNames = [],
    dynamicRooms = [],
    resources = [],
    roomNames = [],
    serviceTypes = [],
    defaultPharmacyServices = [],
    dynamicResources,
    serviceTypesInfo = [],
    members = [],
    clinicians = [],
    currentUserEmail = "",
    handleStartConsultation,

    // filters state
    selectedServiceTypes = [],
    selectedPharmacists = [],
    selectedRooms = [],
    selectedBlockOutTypes = [],
    currentPharmacyId = "",
    googleSyncEvents = [],
    pharmacyRooms = [],
    // utils
    formatDate = (y: number, m: number, d: number) =>
      `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    formatDateFromDate = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(date.getDate()).padStart(2, "0")}`,
    getViewTitle = () => "",
    getDaysInMonth = () => [],
    getWeekDays = () => [],
    getThreeDays = () => [],
    getWorkWeekDays = () => [],
    getFilteredBookingsForDate = () => [],
    getFilteredBookingsForTimeSlot = () => [],
    getFilteredBlockOutsForDate = () => [],
    getFilteredBlockOutsForTimeSlot = () => [],
    filteredTasks = [],
    getFilteredTasksForDate,
    getFilteredTasksForTimeSlot,
    validateBookingConflicts,

    // actions
    navigate = () => {},
    onCalendarSelect = () => {},
    handleViewDay = () => {},
    onCreateBooking = () => {},
    onCreateBookingForDate = () => {},
    onCreateBookingWithDefaults = () => {},
    onShowEditBooking = () => {},
    onEditBooking = () => {},
    onDeleteBooking = () => {},
    onCreateBlockOut = () => {},
    onShowEditBlockOut = () => {},
    onEditBlockOut = () => {},
    onDeleteBlockOut = () => {},
    onSaveFromModal = () => {},
    closeModal = () => {},
    closeBlockOutModal = () => {},
    onSaveFromBlockOutModal = () => {},
    openNewAppointment = () => {},

    // conflict warning
    conflictWarning = null,
    clearConflictWarning = () => {},

    // filter handlers
    handleServiceTypeChange = () => {},
    handleSelectAllServices = () => {},
    handlePharmacistChange = () => {},
    handleSelectAllPharmacists = () => {},
    handleRoomChange = () => {},
    handleSelectAllRooms = () => {},
    handleBlockOutTypeChange = () => {},
    handleSelectAllBlockOutTypes = () => {},
  } = (s || {}) as Partial<UseScheduleDataReturn>;

  // Use Redux-backed Google sync events for display (populated by getGoogleEvents)
  const googleCalendarEvents: GoogleCalendarEvent[] = googleSyncEvents || [];

  const formatTimeSlotLabel = (timeSlot: number) => {
    const hour = Math.floor(timeSlot);
    const minutes = timeSlot % 1 === 0 ? 0 : 30;

    return `${hour.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  // Load saved preferences on mount
  useEffect(() => {
    const savedPreferences = loadSchedulePreferences();
    if (savedPreferences) {
      // Apply saved preferences
      setView(savedPreferences.view);
      setColumnMode(savedPreferences.columnMode);
      setDisplayMode(savedPreferences.displayMode);

      // Store initial preferences
      setInitialPreferences(savedPreferences);
    } else {
      // No saved preferences, set initial state from current values
      setInitialPreferences({
        view,
        columnMode,
        displayMode,
        selectedServiceTypes,
        selectedPharmacists,
        selectedRooms,
        selectedBlockOutTypes,
      });
    }
    setPreferencesLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    if (initialDateString) {
      const d = new Date(initialDateString);
      if (!isNaN(d.getTime())) {
        onCalendarSelect(d);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDateString]);

  // Detect changes to preferences and show save bar
  useEffect(() => {
    if (!preferencesLoaded || !initialPreferences) return;

    const currentPreferences: SchedulePreferences = {
      view,
      columnMode,
      displayMode,
      selectedServiceTypes,
      selectedPharmacists,
      selectedRooms,
      selectedBlockOutTypes,
    };

    // Check if current preferences differ from initial
    const hasChanged =
      currentPreferences.view !== initialPreferences.view ||
      currentPreferences.columnMode !== initialPreferences.columnMode ||
      currentPreferences.displayMode !== initialPreferences.displayMode ||
      JSON.stringify(currentPreferences.selectedServiceTypes.sort()) !==
        JSON.stringify(initialPreferences.selectedServiceTypes.sort()) ||
      JSON.stringify(currentPreferences.selectedPharmacists.sort()) !==
        JSON.stringify(initialPreferences.selectedPharmacists.sort()) ||
      JSON.stringify(currentPreferences.selectedRooms.sort()) !==
        JSON.stringify(initialPreferences.selectedRooms.sort()) ||
      JSON.stringify(currentPreferences.selectedBlockOutTypes.sort()) !==
        JSON.stringify(initialPreferences.selectedBlockOutTypes.sort());

    setShowSaveDefaultBar(hasChanged);
  }, [
    preferencesLoaded,
    initialPreferences,
    view,
    columnMode,
    displayMode,
    selectedServiceTypes,
    selectedPharmacists,
    selectedRooms,
    selectedBlockOutTypes,
  ]);

  const handleSaveDefaultView = () => {
    const preferences: SchedulePreferences = {
      view,
      columnMode,
      displayMode,
      selectedServiceTypes,
      selectedPharmacists,
      selectedRooms,
      selectedBlockOutTypes,
    };

    saveSchedulePreferences(preferences);
    setInitialPreferences(preferences);
    setShowSaveDefaultBar(false);
  };

  const handleDismissSaveBar = () => {
    setShowSaveDefaultBar(false);
  };

  const handleBookingDrop = (
    booking: Booking,
    newDate: string,
    newTime?: string,
  ) => {
    // Drag and drop disabled
    return;
    /*
    if (booking.date === newDate && (!newTime || booking.time === newTime)) {
      return;
    }
    setNotifyPatient(true); // Reset to default (on)
    setRescheduleData({ booking, newDate, newTime });
    */
  };

  const rescheduleConflictMessage = React.useMemo(() => {
    if (!rescheduleData) return null;
    const { booking, newDate, newTime } = rescheduleData;
    const targetTime = newTime || booking.time;
    const targetDuration = String(booking.duration || 30);

    if (validateBookingConflicts) {
      const res = validateBookingConflicts(
        {
          date: newDate,
          time: targetTime,
          duration: targetDuration,
          pharmacist: booking.pharmacist,
          room: booking.room,
        },
        booking.id || (booking as any)._id,
      );
      if (!res.isValid) {
        return res.message;
      }
    }
    return null;
  }, [rescheduleData, validateBookingConflicts]);

  const handleConfirmReschedule = () => {
    if (rescheduleConflictMessage) {
      return;
    }
    if (rescheduleData) {
      const updatedBooking = {
        ...rescheduleData.booking,
        date: rescheduleData.newDate,
        ...(rescheduleData.newTime && { time: rescheduleData.newTime }),
        duration: rescheduleData.booking.duration.toString(),
      };
      onEditBooking(rescheduleData.booking.id, updatedBooking);

      // Trigger notification animation
      // Show message icon if notifying patient, otherwise show check icon
      setNotificationType(notifyPatient ? "message" : "check");
      setNotifiedBookingId(rescheduleData.booking.id);
      // Clear the animation after 2 seconds
      setTimeout(() => {
        setNotifiedBookingId(null);
      }, 2000);

      setRescheduleData(null);
    }
  };

  const handleCancelReschedule = () => {
    setRescheduleData(null);
  };

  // Determine which pharmacists to display in columns
  // If filters are selected, use those; otherwise, default to all pharmacist names (dynamic)
  const displayPharmacists = React.useMemo(() => {
    if (selectedPharmacists.length > 0) {
      return selectedPharmacists;
    }
    // Default to all pharmacist names when no filters are selected
    return pharmacistNames;
  }, [selectedPharmacists, pharmacistNames]);

  const displayServices = React.useMemo(() => {
    if (selectedServiceTypes.length > 0) {
      return selectedServiceTypes.map((serviceIdOrName) => {
        return (
          serviceNameMap[serviceIdOrName] ||
          (serviceTypesInfo || []).find(
            (s: any) =>
              String(s._id) === String(serviceIdOrName) ||
              String(s.name).toLowerCase() === String(serviceIdOrName).toLowerCase() ||
              String(s.label).toLowerCase() === String(serviceIdOrName).toLowerCase()
          )?.name ||
          (defaultPharmacyServices || []).find(
            (s: any) =>
              String(s._id || s.serviceID) === String(serviceIdOrName) ||
              String(s.serviceName).toLowerCase() === String(serviceIdOrName).toLowerCase()
          )?.serviceName ||
          serviceIdOrName
        );
      });
    }
    return serviceTypes;
  }, [
    selectedServiceTypes,
    serviceTypes,
    serviceNameMap,
    serviceTypesInfo,
    defaultPharmacyServices,
  ]);

  const displayRooms = React.useMemo(() => {
    if (selectedRooms && selectedRooms.length > 0) {
      return selectedRooms.map((roomFilter) => {
        const matchingResource = (resources || dynamicResources || pharmacyRooms || []).find(
          (r: any) =>
            String(r.id || r._id) === String(roomFilter) ||
            String(r.name).toLowerCase() === String(roomFilter).toLowerCase()
        );
        return matchingResource ? matchingResource.name : roomFilter;
      });
    }
    return roomNames;
  }, [selectedRooms, roomNames, resources, dynamicResources, pharmacyRooms]);

  // Compute columns based on column mode
  const weekViewColumns = React.useMemo(() => {
    const viewDays =
      view === "week"
        ? getWeekDays(currentDate)
        : view === "threeday"
          ? getThreeDays(currentDate)
          : view === "workweek"
            ? getWorkWeekDays(currentDate)
            : view === "day"
              ? [currentDate]
              : getWeekDays(currentDate);

    if (columnMode === "practitioner") {
      return displayPharmacists;
    } else if (columnMode === "day" || columnMode === "day-practitioner") {
      return viewDays.map((day) => formatDateFromDate(day));
    } else if (columnMode === "services") {
      return displayServices;
    } else if (columnMode === "room") {
      return displayRooms;
    }
    return displayPharmacists;
  }, [
    columnMode,
    displayPharmacists,
    displayServices,
    displayRooms,
    currentDate,
    view,
    serviceTypes,
    roomNames,
    getWeekDays,
    getThreeDays,
    getWorkWeekDays,
    formatDateFromDate,
  ]);

  const handleDurationChange = (bookingId: string, newDuration: number) => {
    // Find the booking across all dates
    const allDates = getDaysInMonth(currentDate)
      .filter((d) => d !== null)
      .map((d) =>
        formatDate(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          d as number,
        ),
      );

    for (const dateString of allDates) {
      const bookings = getFilteredBookingsForDate(dateString);
      const booking = bookings.find((b) => b.id === bookingId);
      if (booking) {
        const updatedBooking = {
          ...booking,
          duration: newDuration,
        };
        onEditBooking(bookingId, updatedBooking);
        return;
      }
    }
  };

  const handleBlockOutDurationChange = (
    blockOutId: string,
    newEndTime: string,
  ) => {
    // Find the block-out across all dates
    const allDates = getDaysInMonth(currentDate)
      .filter((d) => d !== null)
      .map((d) =>
        formatDate(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          d as number,
        ),
      );

    for (const dateString of allDates) {
      const blockOuts = getFilteredBlockOutsForDate(dateString);
      const blockOut = blockOuts.find((bo) => bo.id === blockOutId);
      if (blockOut) {
        const updatedBlockOut = {
          ...blockOut,
          endTime: newEndTime,
        };
        onEditBlockOut(blockOutId, updatedBlockOut);
        return;
      }
    }
  };

  // Helper function to get block-outs for a specific time slot (including half hours)
  const getBlockOutsForTimeSlot = (dateString: string, timeSlot: number) => {
    return getFilteredBlockOutsForTimeSlot(dateString, timeSlot);
  };

  // Sync remote / filtered tasks strictly into state without local duplicates
  useEffect(() => {
    if ((s as any)?.filteredTasks) {
      setTasks((s as any).filteredTasks);
    } else if ((s as any)?.remoteTasks) {
      setTasks((s as any).remoteTasks);
    }
  }, [(s as any)?.filteredTasks, (s as any)?.remoteTasks]);

  // Helper function to get tasks for a specific time slot
  const getTasksForTimeSlot = (dateString: string, timeSlot: number) => {
    if (getFilteredTasksForTimeSlot) {
      return getFilteredTasksForTimeSlot(dateString, timeSlot);
    }
    const hour = Math.floor(timeSlot);
    const minutes = timeSlot % 1 === 0 ? 0 : 30;

    // Filter tasks that match the date and time
    return tasks.filter((task) => {
      if (task.dueDate !== dateString) return false;
      if (!task.dueTime) return true; // Show on date if no specific time set

      const [tHour, tMin] = task.dueTime.split(":").map(Number);
      return tHour === hour && (minutes === 0 ? tMin < 30 : tMin >= 30);
    });
  };

  // Handler for editing tasks
  const handleEditTask = (taskId: string, task?: Task) => {
    const taskToEdit = task || tasks.find((t) => t.id === taskId);
    if (taskToEdit) {
      setEditingTask(taskToEdit);
      setShowTaskModal(true);
    }
  };

  // Handler for saving task (re-sync with API data only)
  const handleSaveTask = async (_taskData: any) => {
    if (currentPharmacyId) {
      try {
        await refreshScheduleEventsSilently(currentPharmacyId);
      } catch (err) {
        console.error("Failed to refresh schedule events silently:", err);
      }
    }
    setShowTaskModal(false);
    setEditingTask(null);
  };

  if (isEventsLoading) {
    return <ScheduleSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <div
      className={`flex-1 flex flex-col h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-white"
      }`}
    >
      {/* Left Sidebar removed - filters moved to header */}
      {false && (
        <div className="hidden">
          <div className="space-y-6">
            {/* Small Month Calendar */}
            <div>
              <div
                className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 [&_.rdp]:text-white [&_.rdp-button]:text-white [&_.rdp-button]:hover:bg-gray-600"
                    : "border-gray-200 bg-white"
                }`}
              >
                <Calendar
                  mode="single"
                  selected={calendarSelectedDate}
                  onSelect={(date) => onCalendarSelect(date || undefined)}
                  className="w-full scale-90 origin-top"
                  classNames={{
                    day_selected:
                      "bg-purple-600 text-white hover:bg-purple-700",
                    day_today: isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-100 text-gray-900",
                    ...(isDarkMode && {
                      months:
                        "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                      month: "space-y-4",
                      caption:
                        "flex justify-center pt-1 relative items-center text-white",
                      caption_label: "text-sm font-medium text-white",
                      nav_button:
                        "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-white hover:bg-gray-700 border border-gray-600 rounded",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex",
                      head_cell:
                        "text-gray-400 rounded-md w-9 font-normal text-xs",
                      row: "flex w-full mt-2",
                      cell: "h-9 w-9 text-center text-sm p-0 relative",
                      day: "h-9 w-9 p-0 font-normal hover:bg-gray-700 text-white rounded",
                      day_outside: "text-gray-600 opacity-50",
                      day_disabled: "text-gray-600 opacity-50",
                    }),
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const dateString = formatDateFromDate(date);
                      const dayBookings = getBookingsForDate(dateString);
                      const hasBookings = dayBookings.length > 0;
                      const hasBlockOuts = dayBlockOuts.length > 0;

                      return (
                        <div className="relative w-full h-full flex items-center justify-center">
                          {date.getDate()}
                          <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 flex gap-1">
                            {hasBookings && (
                              <div
                                className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                                  isDarkMode ? "bg-orange-400" : "bg-orange-500"
                                }`}
                              />
                            )}
                            {hasBlockOuts && (
                              <div
                                className={`w-1.5 h-1.5 transition-colors duration-300 ${
                                  isDarkMode ? "bg-red-400" : "bg-red-500"
                                }`}
                                style={{
                                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    },
                  }}
                  onDayClick={(date) => onCalendarSelect(date)}
                />
              </div>
            </div>

            {/* Filters Header */}
            <div className="flex items-center gap-2">
              <FilterIcon
                className={`w-4 h-4 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              />
              <h3
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Filters
              </h3>
            </div>

            {/* Service Type Filters */}
            <ServiceTypeFilter
              selectedServiceTypes={selectedServiceTypes}
              onServiceTypeChange={handleServiceTypeChange}
              onSelectAll={handleSelectAllServices}
              isDarkMode={isDarkMode}
            />

            {/* Pharmacist Filters */}
            <PharmacistFilter
              selectedPharmacists={selectedPharmacists}
              onPharmacistChange={handlePharmacistChange}
              onSelectAll={handleSelectAllPharmacists}
              pharmacists={pharmacistNames}
              isDarkMode={isDarkMode}
            />

            {/* Room Filters */}
            <RoomFilter
              selectedRooms={selectedRooms}
              onRoomChange={handleRoomChange}
              onSelectAll={handleSelectAllRooms}
              roomNames={roomNames}
              isDarkMode={isDarkMode}
            />

            {/* Block-Out Types Filters */}
            <BlockOutTypesFilter
              selectedBlockOutTypes={selectedBlockOutTypes}
              onBlockOutTypeChange={handleBlockOutTypeChange}
              onSelectAll={handleSelectAllBlockOutTypes}
              isDarkMode={isDarkMode}
            />

            {/* Service Types Info */}
            <ServiceTypesInfo isDarkMode={isDarkMode} />
          </div>
        </div>
      )}

      {/* Main Content Area - Non-scrollable */}
      <div className="flex-1 flex flex-col max-h-screen overflow-hidden">
        {/* Calendar Header */}
        <div
          className={`border-b p-6 transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            {/* Left side - Action buttons & Filters */}
            <div className="flex items-center gap-2">
              {/* Filters Button */}
              <ScheduleFilters
                isDarkMode={isDarkMode}
                selectedServiceTypes={selectedServiceTypes}
                onServiceTypeChange={handleServiceTypeChange}
                onSelectAllServices={handleSelectAllServices}
                serviceTypes={serviceTypes}
                selectedPharmacists={selectedPharmacists}
                onPharmacistChange={handlePharmacistChange}
                onSelectAllPharmacists={handleSelectAllPharmacists}
                pharmacists={pharmacistNames}
                selectedRooms={selectedRooms}
                onRoomChange={handleRoomChange}
                onSelectAllRooms={handleSelectAllRooms}
                roomNames={roomNames}
                selectedBlockOutTypes={selectedBlockOutTypes}
                onBlockOutTypeChange={handleBlockOutTypeChange}
                onSelectAllBlockOutTypes={handleSelectAllBlockOutTypes}
              />
              {/* Add Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-slate-900 hover:bg-slate-800 gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className={`${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <DropdownMenuItem
                    onClick={openNewAppointment}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "hover:bg-gray-700 focus:bg-gray-700"
                        : "hover:bg-gray-100 focus:bg-gray-100"
                    }`}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <span>New Appointment</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowBlockOutModal(true)}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "hover:bg-gray-700 focus:bg-gray-700"
                        : "hover:bg-gray-100 focus:bg-gray-100"
                    }`}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    <span>Block-Out Period</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingTask(null);
                      setShowTaskModal(true);
                    }}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "hover:bg-gray-700 focus:bg-gray-700"
                        : "hover:bg-gray-100 focus:bg-gray-100"
                    }`}
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    <span>Task</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Center - Month navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("prev")}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={`text-lg font-medium min-w-[200px] text-center transition-colors duration-300 hover:opacity-75 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {getViewTitle()}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className={`w-auto p-0 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <Calendar
                    mode="single"
                    selected={calendarSelectedDate}
                    onSelect={(date) => {
                      if (date) {
                        onCalendarSelect(date);
                      }
                    }}
                    classNames={{
                      day_selected:
                        "bg-purple-600 text-white hover:bg-purple-700",
                      day_today: isDarkMode
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-100 text-gray-900",
                      ...(isDarkMode && {
                        months:
                          "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption:
                          "flex justify-center pt-1 relative items-center text-white",
                        caption_label: "text-sm font-medium text-white",
                        nav_button:
                          "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-white hover:bg-gray-700 border border-gray-600 rounded",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell:
                          "text-gray-400 rounded-md w-9 font-normal text-xs",
                        row: "flex w-full mt-2",
                        cell: "h-9 w-9 text-center text-sm p-0 relative",
                        day: "h-9 w-9 p-0 font-normal hover:bg-gray-700 text-white rounded",
                        day_outside: "text-gray-600 opacity-50",
                        day_disabled: "text-gray-600 opacity-50",
                      }),
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("next")}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Right side - View toggle */}
            <div className="flex items-center gap-3">
              {/* Calendar Sync Button */}
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSyncModal(true)}
                title="Sync with external calendars"
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Sync Calendar</span>
                {googleSyncState?.accessToken && (
                  <span className="ml-1 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                      Connected
                    </span>
                  </span>
                )}
              </Button> */}

              {/* View Settings - combines view type, group by, and display mode */}
              <ViewSettings
                isDarkMode={isDarkMode}
                view={view}
                onViewChange={setView}
                columnMode={columnMode}
                onColumnModeChange={setColumnMode}
                displayMode={displayMode}
                onDisplayModeChange={setDisplayMode}
                showColumnMode={
                  (view === "day" ||
                    view === "week" ||
                    view === "threeday" ||
                    view === "workweek") &&
                  displayMode === "calendar"
                }
              />
            </div>
          </div>
        </div>

        {/* Calendar Views */}
        <div
          className={`flex-1 p-6 overflow-y-auto transition-colors duration-300 ${
            isDarkMode ? "bg-gray-900" : ""
          }`}
        >
          {view === "month" && displayMode === "calendar" && (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-px mb-px">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className={`p-3 text-center text-sm font-medium transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-50 text-gray-700"
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div
                className={`grid grid-cols-7 gap-px transition-colors duration-300 ${
                  isDarkMode ? "bg-gray-600" : "bg-gray-200"
                }`}
              >
                {getDaysInMonth(currentDate).map((day, index) => {
                  if (day === null) {
                    return (
                      <div
                        key={`empty-${index}`}
                        className={`h-36 p-2 transition-colors duration-300 ${
                          isDarkMode ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      />
                    );
                  }

                  const dateString = formatDate(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    day,
                  );
                  const dayBookings = getBookingsForDate(dateString);
                  const dayBlockOuts = getFilteredBlockOutsForDate(dateString);
                  const dayTasks = getFilteredTasksForDate
                    ? getFilteredTasksForDate(dateString)
                    : tasks.filter((task) => task.dueDate === dateString);
                  const isToday =
                    new Date().toDateString() ===
                    new Date(dateString).toDateString();

                  const dayDate = new Date(`${dateString}T00:00:00`);
                  const isDayWorking = checkSlotWorkingStatus(
                    dayDate,
                    undefined,
                    "day",
                    dateString,
                    selectedPharmacists,
                    selectedRooms,
                    selectedServiceTypes,
                    resources,
                    pharmacistNames,
                    roomNames,
                  );

                  return (
                    <CalendarDayCell
                      key={day}
                      day={day}
                      dateString={dateString}
                      dayBookings={dayBookings}
                      dayBlockOuts={dayBlockOuts}
                      dayTasks={dayTasks}
                      isToday={isToday}
                      isNonWorking={!isDayWorking}
                      serviceColors={serviceColors}
                      serviceNameMap={serviceNameMap}
                      serviceColorMap={serviceColorMap}
                      dayGoogleEvents={getDayGoogleEvents(dateString)}
                      onCreateBooking={onCreateBooking}
                      onEditBooking={onShowEditBooking}
                      onDeleteBooking={onDeleteBooking}
                      onStartConsultation={handleStartConsultation}
                      onEditBlockOut={onShowEditBlockOut}
                      onEditTask={handleEditTask}
                      onViewDay={handleViewDay}
                      onCreateBookingForDate={onCreateBookingForDate}
                      pharmacists={pharmacistNames}
                      rooms={roomNames}
                      isDarkMode={isDarkMode}
                      onBookingDrop={handleBookingDrop}
                      notifiedBookingId={notifiedBookingId}
                      notificationType={notificationType}
                    />
                  );
                })}
              </div>
            </>
          )}

          {view === "month" && displayMode === "list" && (
            <div
              className={`rounded-lg border overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className={`overflow-x-auto`}>
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Date
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Time
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 w-48 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Patient
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Service Type
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Pharmacist
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Actions
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Vaccines Required
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getDaysInMonth(currentDate).map((day, index) => {
                      if (day === null) return null;

                      const dateString = formatDate(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        day,
                      );
                      const dayBookings = getBookingsForDate(dateString);

                      return dayBookings.map((booking) => {
                        const matchedService = defaultPharmacyServices?.find(
                          (service) => service._id === booking.serviceId,
                        );

                        const serviceName = matchedService?.serviceName || "";
                        const serviceColor =
                          matchedService?.serviceColor ||
                          booking.serviceColor ||
                          "";

                        const bookingDate = new Date(dateString);
                        const dayOfWeek = [
                          "Sun",
                          "Mon",
                          "Tue",
                          "Wed",
                          "Thu",
                          "Fri",
                          "Sat",
                        ][bookingDate.getDay()];
                        const month = String(
                          bookingDate.getMonth() + 1,
                        ).padStart(2, "0");
                        const dayNum = String(bookingDate.getDate()).padStart(
                          2,
                          "0",
                        );
                        const formattedDate = `${month}/${dayNum} ${dayOfWeek}`;

                        const [startHour, startMin] = booking.time
                          .split(":")
                          .map(Number);
                        const endMinutes =
                          startHour * 60 + startMin + booking.duration;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;

                        const formatTime = (hour: number, min: number) => {
                          const period = hour >= 12 ? "pm" : "am";
                          const displayHour =
                            hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                          return min === 0
                            ? `${displayHour}${period}`
                            : `${displayHour}:${String(min).padStart(2, "0")}${period}`;
                        };

                        const timeRange = `${formatTime(startHour, startMin)}-${formatTime(endHour, endMin)}`;

                        return (
                          <tr
                            key={`${dateString}-${booking.id}`}
                            className={`border-b transition-colors duration-300 hover:${
                              isDarkMode ? "bg-gray-700" : "bg-gray-50"
                            } cursor-pointer ${
                              isDarkMode ? "border-gray-600" : "border-gray-200"
                            }`}
                            onClick={() =>
                              onShowEditBooking(booking.id, booking)
                            }
                          >
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {formattedDate}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {timeRange}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 max-w-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              <div className="truncate font-medium">
                                {booking.patientName}
                              </div>
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {serviceName?.trim() ? (
                                <span
                                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: `${serviceColor}20`,
                                    color: serviceColor,
                                  }}
                                >
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: serviceColor }}
                                  />
                                  {serviceName}
                                </span>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.pharmacist}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.tasksToComplete &&
                              booking.tasksToComplete.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {booking.tasksToComplete.map(
                                    (task: any, idx: number) => {
                                      const label =
                                        task?.label ||
                                        task?.text ||
                                        task?.name ||
                                        (typeof task === "string"
                                          ? task
                                          : String(task));
                                      return (
                                        <span
                                          key={task?.id || idx}
                                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            isDarkMode
                                              ? "bg-blue-900/30 text-blue-300"
                                              : "bg-blue-100 text-blue-800"
                                          }`}
                                          title={label}
                                        >
                                          {label}
                                        </span>
                                      );
                                    },
                                  )}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {(booking.vaccinesToAdminister ||
                                booking.vaccinesAdminister) &&
                              ((booking.vaccinesToAdminister &&
                                booking.vaccinesToAdminister.length > 0) ||
                                (booking.vaccinesAdminister &&
                                  booking.vaccinesAdminister.length > 0)) ? (
                                <div className="flex flex-wrap gap-1">
                                  {(
                                    booking.vaccinesToAdminister ||
                                    booking.vaccinesAdminister ||
                                    []
                                  ).map((vaccine: any, idx: number) => {
                                    const name =
                                      vaccine?.vaccineName ||
                                      vaccine?.VaccineName ||
                                      vaccine?.name ||
                                      vaccine?.Name ||
                                      (typeof vaccine === "string"
                                        ? vaccine
                                        : String(vaccine));
                                    return (
                                      <span
                                        key={vaccine?.id || vaccine?.Id || idx}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isDarkMode
                                            ? "bg-amber-900/30 text-amber-300"
                                            : "bg-amber-100 text-amber-800"
                                        }`}
                                        title={
                                          vaccine?.dose || vaccine?.Manufacturer
                                            ? `${vaccine.dose || vaccine.Manufacturer}`
                                            : ""
                                        }
                                      >
                                        {name}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(view === "week" ||
            view === "threeday" ||
            view === "workweek" ||
            view === "day") &&
            displayMode === "calendar" && (
              <div
                className={`border rounded-lg overflow-x-auto transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Column Headers */}
                <div
                  className={`grid border-b transition-colors duration-300 ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                  style={{
                    gridTemplateColumns: `120px repeat(${weekViewColumns.length}, minmax(150px, 1fr))`,
                  }}
                >
                  <div
                    className={`p-3 border-r transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Time
                    </div>
                  </div>
                  {weekViewColumns.map((column, index) => {
                    if (columnMode === "practitioner") {
                      const pharmacistName = column;
                      return (
                        <div
                          key={index}
                          className={`p-3 text-center border-r transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {pharmacistName}
                          </div>
                        </div>
                      );
                    } else if (
                      columnMode === "day" ||
                      columnMode === "day-practitioner"
                    ) {
                      const dateString = column;
                      const dayShort = getDayShortName(dateString);
                      const date = new Date(dateString);
                      const isToday =
                        new Date().toDateString() === date.toDateString();
                      return (
                        <div
                          key={index}
                          className={`p-3 text-center border-r transition-colors duration-300 ${
                            isToday
                              ? isDarkMode
                                ? "bg-blue-900/30 border-gray-600"
                                : "bg-blue-50 border-gray-200"
                              : isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isToday
                                ? isDarkMode
                                  ? "text-blue-300"
                                  : "text-blue-600"
                                : isDarkMode
                                  ? "text-white"
                                  : "text-gray-900"
                            }`}
                          >
                            {dayShort}
                          </div>
                          <div
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {date.getDate()}
                          </div>
                        </div>
                      );
                    } else if (columnMode === "services") {
                      const serviceType = column;
                      return (
                        <div
                          key={index}
                          className={`p-3 text-center border-r transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {serviceType}
                          </div>
                        </div>
                      );
                    } else if (columnMode === "room") {
                      const roomName = column;
                      return (
                        <div
                          key={index}
                          className={`p-3 text-center border-r transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {roomName}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Sub-Headers Row - Only show for practitioner, services, and room modes */}
                {(columnMode === "practitioner" ||
                  columnMode === "services" ||
                  columnMode === "room") && (
                  <div
                    className={`grid border-b transition-colors duration-300 ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-800"
                        : "border-gray-200 bg-gray-100"
                    }`}
                    style={{
                      gridTemplateColumns: `120px repeat(${weekViewColumns.length}, minmax(150px, 1fr))`,
                    }}
                  >
                    <div
                      className={`p-2 border-r transition-colors duration-300 ${
                        isDarkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                    />
                    {weekViewColumns.map((column, colIndex) => {
                      const viewDays =
                        view === "week"
                          ? getWeekDays(currentDate)
                          : view === "threeday"
                            ? getThreeDays(currentDate)
                            : view === "workweek"
                              ? getWorkWeekDays(currentDate)
                              : view === "day"
                                ? [currentDate]
                                : getWeekDays(currentDate);
                      const numDays = viewDays.length;
                      return (
                        <div
                          key={colIndex}
                          className={`grid border-r transition-colors duration-300 ${
                            isDarkMode ? "border-gray-600" : "border-gray-200"
                          }`}
                          style={{
                            gridTemplateColumns: `repeat(${numDays}, 1fr)`,
                          }}
                        >
                          {viewDays.map((day, dayIndex) => {
                            const dateString = formatDateFromDate(day);
                            const dayShort = getDayShortName(dateString);
                            const isToday =
                              new Date().toDateString() === day.toDateString();

                            return (
                              <div
                                key={dayIndex}
                                className={`p-1 text-center text-xs font-medium border-r last:border-r-0 transition-colors duration-300 ${
                                  isToday
                                    ? isDarkMode
                                      ? "bg-blue-900/30 text-blue-300"
                                      : "bg-blue-50 text-blue-600"
                                    : isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                } ${
                                  isDarkMode
                                    ? "border-gray-700"
                                    : "border-gray-300"
                                }`}
                              >
                                <div>{dayShort}</div>
                                <div
                                  className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                                >
                                  {day.getDate()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Sub-Headers Row - Practitioners nested under each day, for day-practitioner mode */}
                {columnMode === "day-practitioner" && (
                  <div
                    className={`grid border-b transition-colors duration-300 ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-800"
                        : "border-gray-200 bg-gray-100"
                    }`}
                    style={{
                      gridTemplateColumns: `120px repeat(${weekViewColumns.length}, 1fr)`,
                    }}
                  >
                    <div
                      className={`p-2 border-r transition-colors duration-300 ${
                        isDarkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                    />
                    {weekViewColumns?.map((column, colIndex) => (
                      <div
                        key={colIndex}
                        className={`grid border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                        style={{
                          gridTemplateColumns: `repeat(${displayPharmacists.length}, 1fr)`,
                        }}
                      >
                        {displayPharmacists?.map((pharmacistName, pIndex) => (
                          <div
                            key={pIndex}
                            className={`p-1 text-center text-xs font-medium border-r last:border-r-0 truncate transition-colors duration-300 ${
                              isDarkMode
                                ? "text-gray-400 border-gray-700"
                                : "text-gray-600 border-gray-300"
                            }`}
                            title={pharmacistName}
                          >
                            {getPractitionerInitials(pharmacistName)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Time Slots with Pharmacist Columns */}
                {weekTimeSlots.map((timeSlot) => {
                  const timeLabel = formatTimeSlotLabel(timeSlot);

                  return (
                    <div
                      key={timeSlot}
                      className={`grid border-b transition-colors duration-300 relative overflow-visible ${
                        isDarkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                      style={{
                        gridTemplateColumns: `120px repeat(${weekViewColumns.length}, minmax(150px, 1fr))`,
                      }}
                    >
                      <div
                        className={`p-2 border-r transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {timeLabel}
                        </div>
                      </div>

                      {weekViewColumns.map((column, columnIndex) => {
                        const viewDays =
                          view === "week"
                            ? getWeekDays(currentDate)
                            : view === "threeday"
                              ? getThreeDays(currentDate)
                              : view === "workweek"
                                ? getWorkWeekDays(currentDate)
                                : view === "day"
                                  ? [currentDate]
                                  : getWeekDays(currentDate);

                        const weekBookingsByDay: Record<string, Booking[]> = {};
                        const weekBlockOutsByDay: Record<string, any[]> = {};
                        const weekTasksByDay: Record<string, Task[]> = {};
                        const weekGoogleEventsByDay: Record<
                          string,
                          GoogleCalendarEvent[]
                        > = {};
                        const nonWorkingHoursByDay: Record<string, boolean> =
                          {};

                        const timeString = timeLabel;

                        if (columnMode === "practitioner") {
                          // Practitioner mode: Group bookings by day for this practitioner
                          const pharmacistName = column;

                          viewDays.forEach((day) => {
                            const dateString = formatDateFromDate(day);
                            const slotBookings = getBookingsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const pharmacistBookings = slotBookings.filter(
                              (b) => b.pharmacist === pharmacistName,
                            );
                            weekBookingsByDay[dateString] = pharmacistBookings;

                            const slotBlockOuts = getBlockOutsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const pharmacistBlockOuts = slotBlockOuts.filter(
                              (bo) => {
                                if (!bo.affectedPharmacists || bo.affectedPharmacists.length === 0) return false;
                                const normP = String(pharmacistName).trim().toLowerCase();
                                const res = (resources || []).find(
                                  (r: any) => String(r.name).trim().toLowerCase() === normP
                                );
                                const pId = res ? String(res.id).trim().toLowerCase() : normP;

                                return bo.affectedPharmacists.some((p: any) => {
                                  const normAff = String(p).trim().toLowerCase();
                                  return normAff === normP || normAff === pId;
                                });
                              }
                            );
                            weekBlockOutsByDay[dateString] =
                              pharmacistBlockOuts;

                            const slotTasks = getTasksForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const pharmacistTasks = slotTasks.filter((t) => {
                              const normP = (pharmacistName || "")
                                .trim()
                                .toLowerCase();
                              const normAssigned = (t.assignedTo || "")
                                .trim()
                                .toLowerCase();
                              const pResource = resources.find(
                                (r: any) =>
                                  r.name &&
                                  r.name.trim().toLowerCase() === normP,
                              );
                              const pResId = (pResource?.id || "")
                                .trim()
                                .toLowerCase();

                              return (
                                normAssigned === normP ||
                                (pResId && normAssigned === pResId) ||
                                !t.assignedTo ||
                                t.assignedTo === ""
                              );
                            });
                            weekTasksByDay[dateString] = pharmacistTasks;

                            // Add Google Calendar events for this time slot
                            const slotGoogleEvents = getGoogleEventsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekGoogleEventsByDay[dateString] =
                              slotGoogleEvents;

                            const isWorking = checkSlotWorkingStatus(
                              day,
                              timeString,
                              columnMode,
                              column,
                              selectedPharmacists,
                              selectedRooms,
                              selectedServiceTypes,
                              resources,
                              pharmacistNames,
                              roomNames,
                            );
                            nonWorkingHoursByDay[dateString] = !isWorking;
                          });
                        } else if (columnMode === "day") {
                          // Day mode: Show all practitioners' bookings for this day
                          const dateString = column;
                          const slotBookings = getBookingsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBookingsByDay[dateString] = slotBookings;

                          const slotBlockOuts = getBlockOutsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBlockOutsByDay[dateString] = slotBlockOuts;

                          const slotTasks = getTasksForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekTasksByDay[dateString] = slotTasks;

                          // Add Google Calendar events for this time slot
                          const slotGoogleEvents = getGoogleEventsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekGoogleEventsByDay[dateString] = slotGoogleEvents;

                          const dayObj = new Date(`${dateString}T00:00:00`);
                          const isWorking = checkSlotWorkingStatus(
                            dayObj,
                            timeString,
                            columnMode,
                            column,
                            selectedPharmacists,
                            selectedRooms,
                            selectedServiceTypes,
                            resources,
                            pharmacistNames,
                            roomNames,
                          );
                          nonWorkingHoursByDay[dateString] = !isWorking;
                        } else if (columnMode === "services") {
                          // Services mode: Group bookings by day for this service type
                          const serviceType = column;

                          viewDays.forEach((day) => {
                            const dateString = formatDateFromDate(day);
                            const slotBookings = getBookingsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const serviceBookings = slotBookings.filter(
                              (b) => {
                                const bName = b.serviceId
                                  ? serviceNameMap[b.serviceId]
                                  : b.serviceType;
                                return (
                                  b.serviceType === serviceType ||
                                  b.serviceId === serviceType ||
                                  (bName &&
                                    bName.trim().toLowerCase() ===
                                      serviceType.trim().toLowerCase())
                                );
                              },
                            );
                            weekBookingsByDay[dateString] = serviceBookings;

                            const slotBlockOuts = getBlockOutsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekBlockOutsByDay[dateString] = slotBlockOuts;

                            const slotTasks = getTasksForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekTasksByDay[dateString] = slotTasks;

                            // Add Google Calendar events for this time slot
                            const slotGoogleEvents = getGoogleEventsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekGoogleEventsByDay[dateString] =
                              slotGoogleEvents;

                            const isWorking = checkSlotWorkingStatus(
                              day,
                              timeString,
                              columnMode,
                              column,
                              selectedPharmacists,
                              selectedRooms,
                              selectedServiceTypes,
                              resources,
                              pharmacistNames,
                              roomNames,
                            );
                            nonWorkingHoursByDay[dateString] = !isWorking;
                          });
                        } else if (columnMode === "room") {
                          // Room mode: Group bookings by day for this room
                          const roomName = column;

                          viewDays.forEach((day) => {
                            const dateString = formatDateFromDate(day);
                            const slotBookings = getBookingsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const roomBookings = slotBookings.filter(
                              (b) => {
                                const bRoom = b.room || (b as any).roomId || (b as any).RoomId;
                                const bRoomId = (b as any).roomId || (b as any).RoomId || b.room;
                                const resolvedRoomName =
                                  (s as any)?.roomNameMap?.[String(bRoomId)] ||
                                  (s as any)?.roomNameMap?.[String(bRoom)] ||
                                  bRoom;

                                const normCol = String(roomName).trim().toLowerCase();
                                return (
                                  normCol === String(bRoom).trim().toLowerCase() ||
                                  normCol === String(bRoomId).trim().toLowerCase() ||
                                  normCol === String(resolvedRoomName).trim().toLowerCase()
                                );
                              }
                            );
                            weekBookingsByDay[dateString] = roomBookings;

                            const slotBlockOuts = getBlockOutsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const roomBlockOuts = slotBlockOuts.filter(
                              (bo) => {
                                const hasAffectedRooms = bo.affectedRooms && bo.affectedRooms.length > 0;
                                const hasAffectedPharmacists = bo.affectedPharmacists && bo.affectedPharmacists.length > 0;

                                if (hasAffectedRooms) {
                                  return bo.affectedRooms.some((r: any) => {
                                    const rName = (s as any)?.roomNameMap?.[String(r)] || r;
                                    const normCol = String(roomName).trim().toLowerCase();
                                    return (
                                      normCol === String(r).trim().toLowerCase() ||
                                      normCol === String(rName).trim().toLowerCase()
                                    );
                                  });
                                }

                                if (!hasAffectedRooms && !hasAffectedPharmacists) {
                                  return true;
                                }

                                return false;
                              }
                            );
                            weekBlockOutsByDay[dateString] = roomBlockOuts;

                            const slotTasks = getTasksForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            const roomTasks = slotTasks.filter((t: any) => {
                              const tRoom = t.room || t.roomId || t.affectedRooms;
                              if (!tRoom) return false;
                              const roomsArray = Array.isArray(tRoom) ? tRoom : [tRoom];
                              return roomsArray.some((r: any) => {
                                const rName = (s as any)?.roomNameMap?.[String(r)] || r;
                                const normCol = String(roomName).trim().toLowerCase();
                                return (
                                  normCol === String(r).trim().toLowerCase() ||
                                  normCol === String(rName).trim().toLowerCase()
                                );
                              });
                            });
                            weekTasksByDay[dateString] = roomTasks;

                            // Add Google Calendar events for this time slot
                            const slotGoogleEvents = getGoogleEventsForTimeSlot(
                              dateString,
                              timeSlot,
                            );
                            weekGoogleEventsByDay[dateString] =
                              slotGoogleEvents;

                            const isWorking = checkSlotWorkingStatus(
                              day,
                              timeString,
                              columnMode,
                              column,
                              selectedPharmacists,
                              selectedRooms,
                              selectedServiceTypes,
                              resources,
                              pharmacistNames,
                              roomNames,
                            );
                            nonWorkingHoursByDay[dateString] = !isWorking;
                          });
                        } else if (columnMode === "day-practitioner") {
                          // Day mode: Show all practitioners' bookings for this day
                          const dateString = column;
                          const slotBookings = getBookingsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBookingsByDay[dateString] = slotBookings;

                          const slotBlockOuts = getBlockOutsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekBlockOutsByDay[dateString] = slotBlockOuts;

                          const slotTasks = getTasksForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekTasksByDay[dateString] = slotTasks;

                          // Add Google Calendar events for this time slot
                          const slotGoogleEvents = getGoogleEventsForTimeSlot(
                            dateString,
                            timeSlot,
                          );
                          weekGoogleEventsByDay[dateString] = slotGoogleEvents;

                          const dayObj = new Date(`${dateString}T00:00:00`);
                          const isWorking = checkSlotWorkingStatus(
                            dayObj,
                            timeString,
                            columnMode,
                            column,
                            selectedPharmacists,
                            selectedRooms,
                            selectedServiceTypes,
                            resources,
                            pharmacistNames,
                            roomNames,
                          );
                          nonWorkingHoursByDay[dateString] = !isWorking;
                        }

                        const firstDayDateString =
                          columnMode === "day" || columnMode === "day-practitioner"
                            ? column
                            : formatDateFromDate(viewDays[0]);

                        return (
                          <TimeSlotCell
                            key={columnIndex}
                            dateString={firstDayDateString}
                            hour={timeSlot}
                            hourBookings={[]} // Legacy prop, not used when weekBookingsByDay is provided
                            hourBlockOuts={[]} // Legacy prop, not used when weekBlockOutsByDay is provided
                            serviceColors={serviceColors}
                            serviceColorsFromData={serviceColorsFromData}
                            serviceNameMap={serviceNameMap}
                            serviceColorMap={serviceColorMap}
                            onCreateBooking={onCreateBooking}
                            onCreateBookingWithDefaults={
                              onCreateBookingWithDefaults
                            }
                            onEditBooking={onShowEditBooking}
                            onDeleteBooking={onDeleteBooking}
                            onEditBlockOut={onShowEditBlockOut}
                            onEditTask={handleEditTask}
                            onStartConsultation={handleStartConsultation}
                            pharmacists={displayPharmacists}
                            rooms={roomNames}
                            size="week"
                            isDarkMode={isDarkMode}
                            onBookingDrop={handleBookingDrop}
                            onDurationChange={handleDurationChange}
                            onBlockOutDurationChange={
                              handleBlockOutDurationChange
                            }
                            isNonWorkingHours={false}
                            weekBookingsByDay={weekBookingsByDay}
                            weekBlockOutsByDay={weekBlockOutsByDay}
                            weekTasksByDay={weekTasksByDay}
                            weekGoogleEventsByDay={weekGoogleEventsByDay}
                            weekDays={
                              columnMode === "day" || columnMode === "day-practitioner"
                                ? [new Date(`${column}T00:00:00`)]
                                : viewDays
                            }
                            nonWorkingHoursByDay={nonWorkingHoursByDay}
                            columnMode={columnMode}
                            columnValue={column}
                            notifiedBookingId={notifiedBookingId}
                            notificationType={notificationType}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

          {(view === "week" ||
            view === "threeday" ||
            view === "workweek" ||
            view === "day") &&
            displayMode === "list" && (
              <div
                className={`rounded-lg border overflow-hidden transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr
                        className={`border-b transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Date
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Time
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 w-48 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Patient
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Service Type
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Pharmacist
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Actions
                        </th>
                        <th
                          className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Vaccines Required
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(view === "week"
                        ? getWeekDays(currentDate)
                        : view === "threeday"
                          ? getThreeDays(currentDate)
                          : view === "workweek"
                            ? getWorkWeekDays(currentDate)
                            : view === "day"
                              ? [currentDate]
                              : getWeekDays(currentDate)
                      ).map((day) => {
                        const dateString = formatDateFromDate(day);
                        const dayBookings = getBookingsForDate(dateString);

                        return dayBookings.map((booking) => {
                          const matchedService = defaultPharmacyServices?.find(
                            (service) => service._id === booking.serviceId,
                          );

                          const serviceName = matchedService?.serviceName || "";
                          const serviceColor =
                            matchedService?.serviceColor ||
                            booking.serviceColor ||
                            "";

                          const bookingDate = new Date(dateString);
                          const dayOfWeek = [
                            "Sun",
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                          ][bookingDate.getDay()];
                          const month = String(
                            bookingDate.getMonth() + 1,
                          ).padStart(2, "0");
                          const dayNum = String(bookingDate.getDate()).padStart(
                            2,
                            "0",
                          );
                          const formattedDate = `${month}/${dayNum} ${dayOfWeek}`;

                          const [startHour, startMin] = booking.time
                            .split(":")
                            .map(Number);
                          const endMinutes =
                            startHour * 60 + startMin + booking.duration;
                          const endHour = Math.floor(endMinutes / 60);
                          const endMin = endMinutes % 60;

                          const formatTime = (hour: number, min: number) => {
                            const period = hour >= 12 ? "pm" : "am";
                            const displayHour =
                              hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            return min === 0
                              ? `${displayHour}${period}`
                              : `${displayHour}:${String(min).padStart(2, "0")}${period}`;
                          };

                          const timeRange = `${formatTime(startHour, startMin)}-${formatTime(endHour, endMin)}`;

                          return (
                            <tr
                              key={`${dateString}-${booking.id}`}
                              className={`border-b transition-colors duration-300 hover:${
                                isDarkMode ? "bg-gray-700" : "bg-gray-50"
                              } cursor-pointer ${
                                isDarkMode
                                  ? "border-gray-600"
                                  : "border-gray-200"
                              }`}
                              onClick={() =>
                                onShowEditBooking(booking.id, booking)
                              }
                            >
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap font-medium ${
                                  isDarkMode ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {formattedDate}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {timeRange}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 max-w-xs ${
                                  isDarkMode ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                <div className="truncate font-medium">
                                  {booking.patientName}
                                </div>
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {serviceName?.trim() ? (
                                  <span
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                                    style={{
                                      backgroundColor: `${serviceColor}20`,
                                      color: serviceColor,
                                    }}
                                  >
                                    <span
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: serviceColor }}
                                    />
                                    {serviceName}
                                  </span>
                                ) : (
                                  <span
                                    className={`${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    None
                                  </span>
                                )}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {booking.pharmacist}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {booking.tasksToComplete &&
                                booking.tasksToComplete.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {booking.tasksToComplete.map((task) => (
                                      <span
                                        key={task.id}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isDarkMode
                                            ? "bg-blue-900/30 text-blue-300"
                                            : "bg-blue-100 text-blue-800"
                                        }`}
                                        title={task.label}
                                      >
                                        {task.label}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span
                                    className={`${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    None
                                  </span>
                                )}
                              </td>
                              <td
                                className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {booking.vaccinesToAdminister &&
                                booking.vaccinesToAdminister.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {booking.vaccinesToAdminister.map(
                                      (vaccine) => (
                                        <span
                                          key={vaccine.id}
                                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            isDarkMode
                                              ? "bg-amber-900/30 text-amber-300"
                                              : "bg-amber-100 text-amber-800"
                                          }`}
                                          title={
                                            vaccine.dose
                                              ? `${vaccine.dose}`
                                              : ""
                                          }
                                        >
                                          {vaccine.name}
                                        </span>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    className={`${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    None
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {false && view === "day" && displayMode === "calendar" && (
            <div
              className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Day Header with Column Headers */}
              {/* Disabled day view - weekViewColumns reference removed to prevent compile errors */}
              {/*
              <div
                className={`grid border-b transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
                style={{
                  gridTemplateColumns: `120px repeat(${(columnMode === "practitioner" || columnMode === "day" ? pharmacistNames : columnMode === "services" ? serviceTypes.map((s: any) => s.serviceName || s) : roomNames).length}, 1fr)`,
                }}
              >
                <div
                  className={`p-4 border-r transition-colors duration-300 ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`text-lg font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {dayNames[currentDate.getDay()]},{" "}
                    {currentDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
                {(columnMode === "practitioner" || columnMode === "day" ? pharmacistNames : columnMode === "services" ? serviceTypes.map((s: any) => s.serviceName || s) : roomNames).map((column, index) => {
                  if (columnMode === "practitioner" || columnMode === "day") {
                    const pharmacistName = column;
                    return (
                      <div
                        key={index}
                        className={`p-4 text-center border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {pharmacistName}
                        </div>
                      </div>
                    );
                  } else if (columnMode === "services") {
                    const serviceType = column;
                    return (
                      <div
                        key={index}
                        className={`p-4 text-center border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {serviceType}
                        </div>
                      </div>
                    );
                  } else if (columnMode === "room") {
                    const roomName = column;
                    return (
                      <div
                        key={index}
                        className={`p-4 text-center border-r transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {roomName}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Time Slots with Pharmacist Columns */}
              {timeSlots.map((hour) => {
                const dateString = formatDateFromDate(currentDate);
                const timeLabel = formatTimeSlotLabel(hour);

                return (
                  <div
                    key={hour}
                    className={`grid border-b transition-colors duration-300 relative overflow-visible ${
                      isDarkMode ? "border-gray-600" : "border-gray-200"
                    }`}
                    style={{
                      gridTemplateColumns: `120px repeat(${(columnMode === "practitioner" || columnMode === "day" || columnMode === "day-practitioner" ? pharmacistNames : columnMode === "services" ? serviceTypes.map((s: any) => s.serviceName || s) : roomNames).length}, 1fr)`,
                    }}
                  >
                    <div
                      className={`p-2 border-r transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {timeLabel}
                      </div>
                    </div>

                    {weekViewColumns?.map((column, columnIndex) => {
                      const allBookings = getFilteredBookingsForTimeSlot(
                        dateString,
                        hour,
                      );
                      const allBlockOuts = getFilteredBlockOutsForTimeSlot(
                        dateString,
                        hour,
                      );
                      const allTasks = getTasksForTimeSlot(dateString, hour);

                      let hourBookings: Booking[] = [];
                      let hourBlockOuts: any[] = [];
                      let hourTasks: Task[] = [];
                      let isNonWorkingHours = false;

                      if (columnMode === "practitioner") {
                        const pharmacistName = column;
                        hourBookings = allBookings.filter(
                          (b) => b.pharmacist === pharmacistName,
                        );
                        hourBlockOuts = allBlockOuts.filter((bo) => {
                          const matches =
                            !bo.affectedPharmacists ||
                            bo.affectedPharmacists.length === 0 ||
                            bo.affectedPharmacists.includes(pharmacistName);
                          return matches;
                        });

                        const normP = (pharmacistName || "").trim().toLowerCase();
                        const pResource = (resources || []).find(
                          (r: any) => r.name && r.name.trim().toLowerCase() === normP,
                        );
                        const pResId = (pResource?.id || "").trim().toLowerCase();

                        hourTasks = allTasks.filter((t) => {
                          const normAssigned = (t.assignedTo || "").trim().toLowerCase();
                          return (
                            normAssigned === normP ||
                            (pResId && normAssigned === pResId) ||
                            !t.assignedTo ||
                            t.assignedTo === ""
                          );
                        });
                      } else if (
                        columnMode === "day" ||
                        columnMode === "day-practitioner"
                      ) {
                        hourBookings = allBookings;
                        hourBlockOuts = allBlockOuts;
                        hourTasks = allTasks;
                      } else if (columnMode === "services") {
                        const serviceType = column;
                        hourBookings = allBookings.filter((b) => {
                          const bName = b.serviceId
                            ? serviceNameMap[b.serviceId]
                            : b.serviceType;
                          return (
                            b.serviceType === serviceType ||
                            b.serviceId === serviceType ||
                            (bName &&
                              bName.trim().toLowerCase() ===
                                serviceType.trim().toLowerCase())
                          );
                        });
                        hourBlockOuts = allBlockOuts;
                        hourTasks = allTasks;
                      } else if (columnMode === "room") {
                        const roomName = column;
                        hourBookings = allBookings.filter(
                          (b) => b.room === roomName,
                        );
                        hourBlockOuts = allBlockOuts.filter(
                          (bo) =>
                            !bo.affectedRooms ||
                            bo.affectedRooms.length === 0 ||
                            bo.affectedRooms.includes(roomName),
                        );
                        hourTasks = allTasks;
                      }

                      const isWorking = checkSlotWorkingStatus(
                        currentDate,
                        timeLabel,
                        columnMode,
                        column,
                        selectedPharmacists,
                        selectedRooms,
                        selectedServiceTypes,
                        resources,
                        pharmacistNames,
                        roomNames,
                      );
                      isNonWorkingHours = !isWorking;

                      return (
                        <TimeSlotCell
                          key={columnIndex}
                          dateString={dateString}
                          hour={hour}
                          hourBookings={hourBookings}
                          hourBlockOuts={hourBlockOuts}
                          hourTasks={hourTasks}
                          serviceColors={serviceColors}
                          serviceColorsFromData={serviceColorsFromData}
                          serviceNameMap={serviceNameMap}
                          serviceColorMap={serviceColorMap}
                          onCreateBooking={onCreateBooking}
                          onCreateBookingWithDefaults={
                            onCreateBookingWithDefaults
                          }
                          onEditBooking={onShowEditBooking}
                          onDeleteBooking={onDeleteBooking}
                          onEditBlockOut={onShowEditBlockOut}
                          onEditTask={handleEditTask}
                          onStartConsultation={handleStartConsultation}
                          pharmacists={pharmacistNames}
                          rooms={roomNames}
                          size="day"
                          isDarkMode={isDarkMode}
                          onBookingDrop={handleBookingDrop}
                          onDurationChange={handleDurationChange}
                          onBlockOutDurationChange={
                            handleBlockOutDurationChange
                          }
                          isNonWorkingHours={isNonWorkingHours}
                          columnMode={columnMode}
                          columnValue={column}
                          notifiedBookingId={notifiedBookingId}
                          notificationType={notificationType}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {false && view === "day" && displayMode === "list" && (
            <div
              className={`rounded-lg border overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <div
                className={`p-4 border-b transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div
                  className={`text-lg font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {dayNames[currentDate.getDay()]},{" "}
                  {currentDate.toLocaleDateString()}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Time
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 w-48 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Patient
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Service Type
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Pharmacist
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Room
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Actions
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Vaccines Required
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getBookingsForDate(formatDateFromDate(currentDate))
                      .sort(
                        (a, b) =>
                          parseInt(a.time.split(":")[0]) -
                          parseInt(b.time.split(":")[0]),
                      )
                      .map((booking) => {
                        const [startHour, startMin] = booking.time
                          .split(":")
                          .map(Number);
                        const endMinutes =
                          startHour * 60 + startMin + booking.duration;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;

                        const formatTime = (hour: number, min: number) => {
                          const period = hour >= 12 ? "pm" : "am";
                          const displayHour =
                            hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                          return min === 0
                            ? `${displayHour}${period}`
                            : `${displayHour}:${String(min).padStart(2, "0")}${period}`;
                        };

                        const timeRange = `${formatTime(startHour, startMin)}-${formatTime(endHour, endMin)}`;

                        return (
                          <tr
                            key={booking.id}
                            className={`border-b transition-colors duration-300 hover:${
                              isDarkMode ? "bg-gray-700" : "bg-gray-50"
                            } cursor-pointer ${
                              isDarkMode ? "border-gray-600" : "border-gray-200"
                            }`}
                            onClick={() =>
                              onShowEditBooking(booking.id, booking)
                            }
                          >
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 whitespace-nowrap ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {timeRange}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 max-w-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              <div className="truncate font-medium">
                                {booking.patientName}
                              </div>
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {serviceName?.trim() ? (
                                <span
                                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: `${serviceColor}20`,
                                    color: serviceColor,
                                  }}
                                >
                                  <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: serviceColor }}
                                  />
                                  {serviceName}
                                </span>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.pharmacist}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.room || "-"}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.tasksToComplete &&
                              booking.tasksToComplete.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {booking.tasksToComplete.map((task) => (
                                    <span
                                      key={task.id}
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        isDarkMode
                                          ? "bg-blue-900/30 text-blue-300"
                                          : "bg-blue-100 text-blue-800"
                                      }`}
                                      title={task.label}
                                    >
                                      {task.label}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {booking.vaccinesToAdminister &&
                              booking.vaccinesToAdminister.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {booking.vaccinesToAdminister.map(
                                    (vaccine) => (
                                      <span
                                        key={vaccine.id}
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isDarkMode
                                            ? "bg-amber-900/30 text-amber-300"
                                            : "bg-amber-100 text-amber-800"
                                        }`}
                                        title={
                                          vaccine.dose ? `${vaccine.dose}` : ""
                                        }
                                      >
                                        {vaccine.name}
                                      </span>
                                    ),
                                  )}
                                </div>
                              ) : (
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calculate service color for modal */}
      {(() => {
        let modalServiceColor = "#9ca3af"; // Default gray
        const booking = modalState.booking;

        // Use the service color directly from the booking (now stored during transformation)
        if (booking?.serviceColor) {
          modalServiceColor = booking.serviceColor;
        }

        return (
          <>
            {/* Booking Modal */}
            <BookingModal
              mode={modalState.mode as "create" | "edit"}
              booking={modalState.booking as any}
              isOpen={modalState.isOpen}
              onSave={onSaveFromModal}
              onDelete={
                modalState.mode === "edit" && modalState.booking
                  ? onDeleteBooking
                  : undefined
              }
              onOpenChange={(open) => {
                if (!open) closeModal();
              }}
              onCancel={closeModal}
              initialDate={modalState.initialDate}
              initialTime={modalState.initialTime}
              initialPharmacistName={modalState.initialPharmacist}
              initialServiceTypeId={
                (() => {
                  if (!modalState.initialServiceType) return undefined;
                  const found = (defaultPharmacyServices || []).find(
                    (s: any) =>
                      s._id === modalState.initialServiceType ||
                      s.serviceName?.trim().toLowerCase() ===
                        String(modalState.initialServiceType)
                          .trim()
                          .toLowerCase(),
                  );
                  return found ? found._id : modalState.initialServiceType;
                })()
              }
              serviceTypes={serviceTypes.map((s: any) => s._id || s.id || s)}
              pharmacists={pharmacistNames}
              rooms={roomNames}
              isDarkMode={isDarkMode}
              isReadOnly={false}
              serviceColor={modalServiceColor}
              onStartConsultation={handleStartConsultation}
            />
          </>
        );
      })()}

      {/* Block Out Edit Modal */}
      <BlockOutModal
        trigger={<div />}
        onSave={onSaveFromBlockOutModal}
        onDelete={
          blockOutModalState.mode === "edit" ? onDeleteBlockOut : undefined
        }
        editMode={
          blockOutModalState.mode === "edit"
            ? blockOutModalState.blockOut
            : undefined
        }
        initialDate={blockOutModalState.initialDate}
        isDarkMode={isDarkMode}
        open={blockOutModalState.isOpen}
        onOpenChange={(open) => {
          if (!open) closeBlockOutModal();
        }}
        pharmacists={pharmacistNames}
        roomNames={dynamicRooms}
        rooms={resources}
      />

      {/* Block Out Modal from Add Dropdown */}
      <BlockOutModal
        trigger={<div />}
        onSave={onCreateBlockOut}
        isDarkMode={isDarkMode}
        open={showBlockOutModal}
        onOpenChange={setShowBlockOutModal}
        pharmacists={pharmacistNames}
        roomNames={dynamicRooms}
        rooms={resources}
      />

      {/* Task Modal from Add Dropdown */}
      <TaskModal
        open={showTaskModal}
        onOpenChange={(open) => {
          setShowTaskModal(open);
          if (!open) {
            setEditingTask(null);
          }
        }}
        onSave={handleSaveTask}
        initialData={editingTask || undefined}
        isDarkMode={isDarkMode}
        pharmacists={pharmacistNames}
        dynamicResources={dynamicResources}
        currentPharmacyId={currentPharmacyId}
      />

      {/* Conflict Warning Modal */}
      {Boolean(conflictWarning) && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open) clearConflictWarning();
          }}
        >
          <DialogContent
            className={`${isDarkMode ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <DialogHeader>
              <DialogTitle
                className={`${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Action Not Allowed
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p
                className={`${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                {conflictWarning}
              </p>
              <div className="flex justify-end">
                <UIButton
                  onClick={clearConflictWarning}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  OK
                </UIButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reschedule Confirmation Modal */}
      <Dialog
        open={Boolean(rescheduleData)}
        onOpenChange={(open) => !open && handleCancelReschedule()}
      >
        <DialogContent
          className={`max-w-md ${isDarkMode ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-900"}`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-xl font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              <svg
                className="w-6 h-6 text-amber-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Reschedule Appointment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Notify Patient Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label
                  htmlFor="notify-patient"
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Notify Patient
                </Label>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Send notification about the reschedule
                </p>
              </div>
              <Switch
                id="notify-patient"
                checked={notifyPatient}
                onCheckedChange={setNotifyPatient}
              />
            </div>

            {rescheduleData && (
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <h4
                  className={`font-medium mb-3 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Appointment Details
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Patient:
                    </span>
                    <span
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {rescheduleData.booking.patientName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Service:
                    </span>
                    <span
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {rescheduleData.booking.serviceId
                        ? serviceNameMap[rescheduleData.booking.serviceId] ||
                          rescheduleData.booking.serviceType
                        : rescheduleData.booking.serviceType}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Time:
                    </span>
                    <span
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {rescheduleData.booking.time}
                    </span>
                  </div>
                  <div className="my-3 border-t border-dashed pt-3">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                      <span className="font-medium">Moving to:</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      New Date:
                    </span>
                    <span
                      className={`font-medium ${
                        isDarkMode ? "text-green-400" : "text-green-600"
                      }`}
                    >
                      {new Date(rescheduleData.newDate).toLocaleDateString(
                        "en-US",
                        {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </div>
                  {rescheduleData.newTime && (
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        New Time:
                      </span>
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {rescheduleData.newTime}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {rescheduleConflictMessage && (
            <div
              className={`p-3 rounded-md border flex items-center gap-2 text-sm font-medium ${
                isDarkMode
                  ? "bg-red-900/30 border-red-700 text-red-200"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{rescheduleConflictMessage}</span>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <UIButton
              variant="outline"
              onClick={handleCancelReschedule}
              className={
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : ""
              }
            >
              Cancel
            </UIButton>
            <UIButton
              onClick={handleConfirmReschedule}
              disabled={Boolean(rescheduleConflictMessage)}
              className={
                rescheduleConflictMessage
                  ? "bg-gray-400 cursor-not-allowed text-white opacity-60"
                  : "bg-gray-900 hover:bg-gray-800 text-white"
              }
            >
              {notifyPatient
                ? "Confirm & Notify Patient"
                : "Confirm Reschedule"}
            </UIButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Sync Modal */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent
          className={`max-w-2xl ${isDarkMode ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-900"}`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Sync Calendar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <p
              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              Connect your calendar to synchronize appointments with external
              calendar services.
            </p>

            {/* Google Calendar */}
            <div
              className={`p-6 rounded-lg border transition-colors ${
                googleSyncState?.accessToken
                  ? isDarkMode
                    ? "bg-green-900/20 border-green-700"
                    : "bg-green-50 border-green-200"
                  : isDarkMode
                    ? "bg-gray-700 border-gray-600 hover:bg-gray-650"
                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      isDarkMode ? "bg-gray-600" : "bg-white"
                    }`}
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3
                        className={`font-semibold text-lg ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Google Calendar
                      </h3>
                      {googleSyncState?.accessToken && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                          Connected
                        </span>
                      )}
                    </div>
                    {googleSyncState?.accessToken ? (
                      <>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Syncing with {googleSyncState.email}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          Last synced:{" "}
                          {new Date(
                            googleSyncState.syncedAt || "",
                          ).toLocaleString()}
                        </p>
                      </>
                    ) : (
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Sync appointments with your Google Calendar account
                      </p>
                    )}
                    <div className="mt-3 space-y-1">
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ✓ Two-way sync
                      </div>
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ✓ Real-time updates
                      </div>
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ✓ Automatic reminders
                      </div>
                    </div>
                  </div>
                </div>
                {googleSyncState?.accessToken ? (
                  <UIButton
                    variant="outline"
                    className={`gap-2 ${
                      isDarkMode
                        ? "border-red-700 text-red-400 hover:bg-red-900/20"
                        : "border-red-300 text-red-600 hover:bg-red-50"
                    }`}
                    onClick={disconnectGoogle}
                  >
                    Disconnect
                  </UIButton>
                ) : (
                  <UIButton
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                    onClick={requestGoogleAccessToken}
                  >
                    Connect
                    <ExternalLink className="w-4 h-4" />
                  </UIButton>
                )}
              </div>
            </div>

            {/* Outlook Calendar */}
            <div
              className={`p-6 rounded-lg border transition-colors ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 hover:bg-gray-650"
                  : "bg-gray-50 border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      isDarkMode ? "bg-gray-600" : "bg-white"
                    }`}
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path
                        fill="#0078D4"
                        d="M20.5 2h-17C2.67 2 2 2.67 2 3.5v17c0 .83.67 1.5 1.5 1.5h17c.83 0 1.5-.67 1.5-1.5v-17c0-.83-.67-1.5-1.5-1.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"
                      />
                      <path
                        fill="#FFF"
                        d="M12 8.5c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3
                      className={`font-semibold text-lg mb-1 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Outlook Calendar
                    </h3>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Sync appointments with Microsoft Outlook Calendar
                    </p>
                    <div className="mt-3 space-y-1">
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ✓ Two-way sync
                      </div>
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ✓ Real-time updates
                      </div>
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ✓ Microsoft Teams integration
                      </div>
                    </div>
                  </div>
                </div>
                <UIButton
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  onClick={() => {
                    // In a real implementation, this would trigger Microsoft OAuth flow
                    alert(
                      "Outlook Calendar sync would be initiated here. This requires Microsoft OAuth integration.",
                    );
                  }}
                >
                  Connect
                  <ExternalLink className="w-4 h-4" />
                </UIButton>
              </div>
            </div>

            {/* Export Options */}
            <div
              className={`p-6 rounded-lg border ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <h3
                className={`font-semibold mb-2 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Manual Export
              </h3>
              <p
                className={`text-sm mb-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Download your appointments as an iCalendar (.ics) file to import
                into any calendar application.
              </p>
              <UIButton
                variant="outline"
                className={`gap-2 ${
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-600"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => {
                  // In a real implementation, this would generate and download an .ics file
                  alert("ICS file export would be generated here.");
                }}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Download .ics File
              </UIButton>
            </div>
          </div>
          <div className="flex justify-end pt-4">
            <UIButton
              variant="outline"
              onClick={() => setShowSyncModal(false)}
              className={
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : ""
              }
            >
              Close
            </UIButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Default View Bar */}
      {/* default viewbar will be hide for now, will be implemented in future */}
      {/* {showSaveDefaultBar && (
        <SaveDefaultViewBar
          isDarkMode={isDarkMode}
          onSave={handleSaveDefaultView}
          onDismiss={handleDismissSaveBar}
        />
      )} */}
    </div>
  );
}
